import { useEffect, useMemo, useState } from "preact/hooks";
import { Combobox } from "../../components/Combobox/Combobox.jsx";
import { inspectRecurringProjectionByMonth } from "../../lib/finance/projectionMath.js";
import { formatCurrency } from "../../lib/formatters.js";
import "./ProjectionFormulaInspector.css";

const monthLabelKeys = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

function normalizeBudgetCategoryValue(row) {
  if (row?.id) {
    return `id:${row.id}`;
  }

  return `name:${String(row?.category ?? "").trim()}`;
}

export function getBudgetCategoryLabel(row) {
  return String(row?.category ?? "").trim() || "Unlabeled";
}

function matchesBudgetCategory(expenseRow, budgetRow) {
  if (budgetRow?.id && expenseRow?.categoryId) {
    return String(expenseRow.categoryId) === String(budgetRow.id);
  }

  return String(expenseRow?.category ?? "").trim() === String(budgetRow?.category ?? "").trim();
}

function normalizeExpenseType(value) {
  return String(value ?? "").trim().toLocaleLowerCase();
}

function formatCount(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2
  });
}

function buildMonthLabel(t, monthIndex) {
  return t(monthLabelKeys[monthIndex] ?? `${monthIndex + 1}`);
}

export function getProjectionFormulaSelectionState(budgetRows = [], selectedCategoryValue = "") {
  const categoryOptions = budgetRows.map((row) => ({
    label: getBudgetCategoryLabel(row),
    value: normalizeBudgetCategoryValue(row)
  })).filter((option) => Boolean(option.label));
  const resolvedCategoryValue = categoryOptions.some((option) => option.value === selectedCategoryValue)
    ? selectedCategoryValue
    : (categoryOptions[0]?.value ?? "");
  const selectedBudgetRow = budgetRows.find((row) => normalizeBudgetCategoryValue(row) === resolvedCategoryValue)
    ?? budgetRows[0]
    ?? null;

  return {
    categoryOptions,
    selectedBudgetRow,
    selectedCategoryValue: resolvedCategoryValue
  };
}

export function getProjectionFormulaProjectionState(expenseRows = [], selectedBudgetRow, dateContext) {
  if (!selectedBudgetRow) {
    return {
      selectedExpenseRows: [],
      projection: inspectRecurringProjectionByMonth([], dateContext)
    };
  }

  const selectedExpenseRows = expenseRows.filter((expenseRow) => (
    normalizeExpenseType(expenseRow.type) === "ongoing"
    && matchesBudgetCategory(expenseRow, selectedBudgetRow)
  ));

  return {
    selectedExpenseRows,
    projection: inspectRecurringProjectionByMonth(selectedExpenseRows, dateContext)
  };
}

export function ProjectionFormulaInspector({
  budgetRows = [],
  expenseRows = [],
  dateContext,
  t = (key) => key
}) {
  const initialState = getProjectionFormulaSelectionState(budgetRows);
  const [selectedCategoryValue, setSelectedCategoryValue] = useState(initialState.selectedCategoryValue);

  useEffect(() => {
    const nextState = getProjectionFormulaSelectionState(budgetRows, selectedCategoryValue);

    if (nextState.selectedCategoryValue !== selectedCategoryValue) {
      setSelectedCategoryValue(nextState.selectedCategoryValue);
    }
  }, [budgetRows, selectedCategoryValue]);
  const { categoryOptions, selectedBudgetRow } = useMemo(
    () => getProjectionFormulaSelectionState(budgetRows, selectedCategoryValue),
    [budgetRows, selectedCategoryValue]
  );
  const { projection } = useMemo(
    () => getProjectionFormulaProjectionState(expenseRows, selectedBudgetRow, dateContext),
    [dateContext, expenseRows, selectedBudgetRow]
  );

  return (
    <div class="projection-formula-inspector">
      <div class="projection-formula-toolbar">
        <label class="projection-formula-field">
          <span class="projection-formula-label">Category</span>
          <Combobox
            options={categoryOptions}
            value={selectedCategoryValue}
            onChange={setSelectedCategoryValue}
            maxVisibleOptions={8}
            placeholder="Choose a category"
          />
        </label>

        {selectedBudgetRow ? (
          <div class="projection-formula-summary">
            <div>{`Recurring estimate: ${formatCurrency(projection.currentSnapshot.implied)}`}</div>
            <div>{`Average occurrences / month: ${formatCount(projection.currentSnapshot.averageMonthlyOccurrences)}`}</div>
          </div>
        ) : null}
      </div>

      {selectedBudgetRow ? (
        <div class="projection-formula-table-wrap" aria-label="Projection breakdown">
          <table class="projection-formula-table">
            <thead>
              <tr>
                <th>Month</th>
                <th class="projection-formula-table-cell-number">Rows</th>
                <th class="projection-formula-table-cell-number">Month spend</th>
                <th class="projection-formula-table-cell-number">Avg occ / mo</th>
                <th class="projection-formula-table-cell-number">Avg spend / occ</th>
                <th class="projection-formula-table-cell-number">Remaining occ</th>
                <th class="projection-formula-table-cell-number">Recurring estimate</th>
                <th class="projection-formula-table-cell-number">Recurring baseline</th>
                <th class="projection-formula-table-cell-number">Projection contribution</th>
              </tr>
            </thead>
            <tbody>
              {projection.months.map((month) => (
                <tr
                  key={month.monthIndex}
                  class={`projection-formula-table-row${month.monthIndex === dateContext.currentMonthIndex ? " projection-formula-table-row--current" : ""}`}
                >
                  <th scope="row">{buildMonthLabel(t, month.monthIndex)}</th>
                  <td class="projection-formula-table-cell-number">{formatCount(month.monthCount)}</td>
                  <td class="projection-formula-table-cell-number">{formatCurrency(month.monthSpend)}</td>
                  <td class="projection-formula-table-cell-number">{formatCount(month.averageMonthlyOccurrences)}</td>
                  <td class="projection-formula-table-cell-number">{formatCurrency(month.averageSpendPerOccurrence)}</td>
                  <td class="projection-formula-table-cell-number">{formatCount(month.remainingExpectedOccurrences)}</td>
                  <td class="projection-formula-table-cell-number">{formatCurrency(month.recurringEstimate)}</td>
                  <td class="projection-formula-table-cell-number">{formatCurrency(month.recurringBaseline)}</td>
                  <td class="projection-formula-table-cell-number">{formatCurrency(month.projectedContribution)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div class="projection-formula-empty">Add budget categories first to inspect the projection math.</div>
      )}
    </div>
  );
}
